import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Inbox, KeyRound, Pin, Plus, Server, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { FormFields } from "@/components/ranger/Crud";
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DANGER } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { ChangePassword } from "@/components/ranger/ChangePassword";
import { api, errMsg } from "@/lib/api";

const SETTING_FIELDS = [
  ["discord_client_id", "Discord application (client) ID"], ["discord_client_secret", "Discord client secret", true],
  ["discord_bot_token", "Discord bot token", true], ["discord_redirect_uri", "OAuth redirect URI"], ["owner_discord_ids", "Owner Discord IDs (comma separated)"],
];
const COMMUNITY_FIELDS = [
  { key: "name", label: "Name", default: "" },
  { key: "mode", label: "Mode", type: "select", options: [["pvp", "PvP"], ["pve_purge", "PvE + monthly purge"]], default: "pvp" },
  { key: "tagline", label: "Tagline", default: "" },
  { key: "description", label: "Description", type: "textarea", default: "" },
];

const Credentials = () => {
  const [s, setS] = useState(null);
  const [f, setF] = useState({});
  useEffect(() => { api.get("/system/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/system/settings", f); setS(data); setF({}); toast.success("Saved. Changes are live — no redeploy needed."); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title="Discord credentials" icon={KeyRound} testId="system-settings-card" className="mb-5">
      <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
        {SETTING_FIELDS.map(([k, label, secret]) => (
          <label key={k} className="space-y-1.5"><span className="rg-label block">{label}</span>
            <Input data-testid={`system-${k}`} type={secret ? "password" : "text"} autoComplete="off" value={f[k] ?? (secret ? "" : s[k].value || "")} placeholder={secret && s[k].set ? "•••••• stored — leave blank to keep" : ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="border-[#1E2D4A] bg-[#050811] font-mono" />
            <span className="block text-xs text-slate-500">Source: {s[k].source}</span></label>
        ))}
        <div className="sm:col-span-2"><button data-testid="system-settings-save" className={BTN_PRIMARY}>Save credentials</button>
          <p className="mt-2 text-xs text-slate-500">Secrets are encrypted in the database. Changing the bot token restarts the bot automatically.</p></div>
      </form>
    </Card>
  );
};

const CommunityRow = ({ c, onChange }) => {
  const [v, setV] = useState(null);
  const [confirm, setConfirm] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.patch(`/system/communities/${c.id}`, v); toast.success("Saved"); setV(null); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  const del = async () => { try { await api.delete(`/system/communities/${c.id}`, { data: { confirm_name: confirm } }); toast.success("Community deleted"); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  const feature = async () => { try { await api.put(`/system/communities/${c.id}/featured`, { featured: !c.featured }); toast.success(c.featured ? "Unpinned from server list" : "Pinned to top of server list"); onChange(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <li data-testid={`system-community-${c.id}`} className="py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><div className="font-display text-lg font-semibold uppercase tracking-wide text-slate-100">{c.name}</div><div className="font-mono text-xs text-slate-500">{c.id} · {c.mode} · RCON {c.rcon.configured ? `${c.rcon.host}:${c.rcon.port}` : "not set"}</div></div>
        <div className="flex gap-2"><button data-testid={`system-community-feature-${c.id}`} onClick={feature} className={c.featured ? BTN_PRIMARY : BTN_SECONDARY}><Pin className="h-4 w-4" />{c.featured ? "Featured" : "Feature"}</button>
        <button data-testid={`system-community-edit-${c.id}`} onClick={() => setV(v ? null : { name: c.name, mode: c.mode, tagline: c.tagline, description: c.description })} className={BTN_SECONDARY}>{v ? "Close" : "Edit"}</button></div>
      </div>
      {v && <form onSubmit={save} className="mt-4 space-y-4"><FormFields fields={COMMUNITY_FIELDS} values={v} setValues={setV} />
        <div className="flex flex-wrap items-center gap-2"><button className={BTN_PRIMARY}>Save</button>
          <Input data-testid={`system-community-confirm-${c.id}`} placeholder={`Type "${c.name}" to delete`} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-64 border-[#1E2D4A] bg-[#050811]" />
          <button type="button" data-testid={`system-community-delete-${c.id}`} disabled={confirm !== c.name} onClick={del} className={BTN_DANGER}><Trash2 className="h-4 w-4" />Delete community & data</button></div>
      </form>}
    </li>
  );
};

const Applications = ({ onChange }) => {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/system/applications").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    setBusy(id);
    try { await api.post(`/system/applications/${id}/${action}`, { note: notes[id] || "" }); toast.success(action === "approve" ? "Approved, server is live" : "Rejected"); load(); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <Card title={`Server applications (${rows.length})`} icon={Inbox} testId="system-applications-card" className="mb-5">
      {rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="system-applications-empty">No pending applications. Owners apply at /setup.</p> : (
        <ul className="divide-y divide-[#1E2D4A]">{rows.map((a) => (
          <li key={a.id} data-testid={`system-application-${a.id}`} className="space-y-2 py-3 text-sm">
            <div className="text-slate-200"><b>{a.name}</b> <span className="text-slate-500">· {a.mode === "pve_purge" ? "PvE + purge" : "PvP"}</span></div>
            <div className="text-xs text-slate-400">By {a.owner_name} ({a.owner_discord_id}) · Discord: {a.guild_name || a.guild_id} · RCON {a.rcon.host}:{a.rcon.port}</div>
            <div className="flex flex-wrap items-center gap-2">
              <Input data-testid={`system-application-note-${a.id}`} placeholder="Note to applicant (optional)" maxLength={300} value={notes[a.id] || ""} onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })} className="max-w-sm" />
              <button data-testid={`system-application-approve-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "approve")} className={BTN_PRIMARY}><Check className="h-4 w-4" />Approve</button>
              <button data-testid={`system-application-reject-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "reject")} className={BTN_DANGER}><X className="h-4 w-4" />Reject</button>
            </div>
          </li>))}</ul>)}
    </Card>
  );
};

export default function System() {
  const { user, reload } = useAuth();
  const [list, setList] = useState([]);
  const [nv, setNv] = useState(null);
  const load = useCallback(() => api.get("/communities").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!user.is_owner) return <EmptyState title="Restricted">Owner only.</EmptyState>;
  const changed = () => { load(); reload(); };
  const create = async (e) => { e.preventDefault(); try { await api.post("/system/communities", nv); toast.success("Community created — add its RCON details on the Servers page"); setNv(null); changed(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div>
      <PageHeader eyebrow="Owner" title="System" description="Live configuration. Everything here applies immediately without redeploying." />
      <Credentials />
      {user.email && <ChangePassword />}
      <Applications onChange={() => { load(); reload(); }} />
      <Card title="Communities & servers" icon={Server} testId="system-communities-card" right={<button data-testid="system-community-add" onClick={() => setNv(nv ? null : { name: "", mode: "pvp", tagline: "", description: "" })} className={BTN_SECONDARY}><Plus className="h-4 w-4" />New community</button>}>
        {nv && <form onSubmit={create} className="mb-5 space-y-4 border-b border-[#1E2D4A] pb-5"><FormFields fields={COMMUNITY_FIELDS} values={nv} setValues={setNv} /><button data-testid="system-community-create" className={BTN_PRIMARY}>Create</button></form>}
        <ul className="divide-y divide-[#1E2D4A]">{list.map((c) => <CommunityRow key={c.id} c={c} onChange={changed} />)}</ul>
        <p className="mt-3 text-xs text-slate-500">After creating a community, switch to it and enter its G-Portal host, port and RCON password on the Servers page.</p>
      </Card>
    </div>
  );
}
